// src/components/admin/DisputeResolutionForm.tsx
// ============================================================
// Summit League v2 — Dispute Resolution Form
//
// Form for admins to resolve disputes. Shows per-game winner
// selection so admins can correct the entire series, then
// determines overall resolution from the game results.
// ============================================================

import React, { useState } from 'react';
import Button from '../ui/Button';

type DisputeStatus = 'open' | 'under_review' | 'resolved_team_a' | 'resolved_team_b' | 'resolved_draw' | 'dismissed';

interface TeamInfo {
  teamId: number;
  teamName: string;
  teamTag: string | null;
}

interface GameInfo {
  matchGameId: number;
  gameNumber: number;
  gameType: string;
  mapName: string;
  teamAReportedWinner: number | null;
  teamBReportedWinner: number | null;
  winnerTeamId: number | null;
  status: string;
}

interface DisputeResolutionFormProps {
  currentStatus: DisputeStatus;
  teamA: TeamInfo;
  teamB: TeamInfo | null;
  games: GameInfo[];
  matchFormat: string;
  isSubmitting: boolean;
  onResolve: (status: DisputeStatus, notes: string, correctedWinnerId?: number, gameResults?: Record<number, number>) => void;
}

export default function DisputeResolutionForm({
  currentStatus,
  teamA,
  teamB,
  games,
  matchFormat,
  isSubmitting,
  onResolve,
}: DisputeResolutionFormProps) {
  const [notes, setNotes] = useState('');
  const [isDismissing, setIsDismissing] = useState(false);

  // Per-game winner selections: { gameNumber: winnerTeamId }
  // Pre-populate with already-confirmed results
  const [gameSelections, setGameSelections] = useState<Record<number, number>>(() => {
    const initial: Record<number, number> = {};
    for (const game of games) {
      if (game.winnerTeamId) {
        initial[game.gameNumber] = game.winnerTeamId;
      }
    }
    return initial;
  });

  const canResolve = currentStatus === 'open' || currentStatus === 'under_review';
  if (!canResolve) return null;

  // Determine how many games need to be won
  const totalGames = games.length;
  const gamesToWin = Math.ceil(totalGames / 2);

  // Calculate series score from selections
  const teamAWins = Object.values(gameSelections).filter((id) => id === teamA.teamId).length;
  const teamBWins = teamB ? Object.values(gameSelections).filter((id) => id === teamB.teamId).length : 0;

  // Determine if enough games have been selected to decide the series
  const seriesDecided = teamAWins >= gamesToWin || teamBWins >= gamesToWin;
  const seriesWinnerId = teamAWins >= gamesToWin ? teamA.teamId : teamBWins >= gamesToWin ? teamB?.teamId : undefined;
  const seriesWinnerName = seriesWinnerId === teamA.teamId ? teamA.teamName : teamB?.teamName;

  // All contested/unconfirmed games must have a selection (up to series-deciding point)
  const allRelevantGamesResolved = (() => {
    let aWins = 0;
    let bWins = 0;
    for (const game of [...games].sort((a, b) => a.gameNumber - b.gameNumber)) {
      if (aWins >= gamesToWin || bWins >= gamesToWin) break;
      const sel = gameSelections[game.gameNumber];
      if (sel === undefined) return false;
      if (sel === teamA.teamId) aWins++;
      else if (sel === teamB?.teamId) bWins++;
    }
    return true;
  })();

  // Validation
  const isValid = isDismissing
    ? notes.trim().length > 0
    : allRelevantGamesResolved && seriesDecided && notes.trim().length > 0;

  const handleGameSelect = (gameNumber: number, winnerTeamId: number) => {
    setGameSelections((prev) => ({ ...prev, [gameNumber]: winnerTeamId }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!notes.trim()) return;

    if (isDismissing) {
      onResolve('dismissed', notes.trim());
      return;
    }

    if (!seriesWinnerId || !seriesDecided) return;

    const resolvedStatus: DisputeStatus = seriesWinnerId === teamA.teamId
      ? 'resolved_team_a'
      : 'resolved_team_b';

    onResolve(resolvedStatus, notes.trim(), seriesWinnerId, gameSelections);
  };

  const getTeamName = (teamId: number): string => {
    if (teamId === teamA.teamId) return teamA.teamName;
    if (teamB && teamId === teamB.teamId) return teamB.teamName;
    return `Team #${teamId}`;
  };

  // Check if a game is still relevant (series not yet decided before it)
  const isGameRelevant = (gameNumber: number): boolean => {
    let aWins = 0;
    let bWins = 0;
    for (const g of [...games].sort((a, b) => a.gameNumber - b.gameNumber)) {
      if (g.gameNumber >= gameNumber) break;
      const sel = gameSelections[g.gameNumber];
      if (sel === teamA.teamId) aWins++;
      else if (sel === teamB?.teamId) bWins++;
    }
    return aWins < gamesToWin && bWins < gamesToWin;
  };

  return (
    <form className="sl-dispute-resolution-form" onSubmit={handleSubmit}>
      <h3 className="sl-dispute-resolution-title">Resolve Dispute</h3>

      {!isDismissing ? (
        <>
          {/* Per-game winner selection */}
          <p style={{ fontSize: '0.82rem', color: '#94a3b8', marginBottom: '16px' }}>
            Select the correct winner for each game in the series. Already confirmed games are pre-filled.
            {totalGames > 1 && (
              <> First to {gamesToWin} wins takes the series ({matchFormat.toUpperCase()}).</>
            )}
          </p>

          <div className="sl-dispute-game-selections">
            {[...games].sort((a, b) => a.gameNumber - b.gameNumber).map((game) => {
              const isConfirmed = game.winnerTeamId !== null;
              const isContested = game.status === 'contested';
              const selectedWinner = gameSelections[game.gameNumber];
              const gameRelevant = isGameRelevant(game.gameNumber);

              return (
                <div
                  key={game.matchGameId}
                  className={`sl-dispute-game-selection-row ${isContested ? 'sl-dispute-game-selection-row--contested' : ''} ${!gameRelevant ? 'sl-dispute-game-selection-row--irrelevant' : ''}`}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    backgroundColor: isContested ? 'rgba(239, 68, 68, 0.06)' : 'rgba(148, 163, 184, 0.04)',
                    border: `1px solid ${isContested ? 'rgba(239, 68, 68, 0.2)' : 'rgba(148, 163, 184, 0.1)'}`,
                    marginBottom: '10px',
                    opacity: gameRelevant ? 1 : 0.4,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#e5e7eb' }}>Game {game.gameNumber}</span>
                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{game.mapName} — {game.gameType}</span>
                    {isContested && (
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '2px 8px', borderRadius: '4px', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#ef4444',
                      }}>Disputed</span>
                    )}
                    {isConfirmed && !isContested && (
                      <span style={{
                        fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '2px 8px', borderRadius: '4px', backgroundColor: 'rgba(74, 222, 128, 0.15)', color: '#4ade80',
                      }}>Confirmed</span>
                    )}
                  </div>

                  {/* What each team reported */}
                  {(game.teamAReportedWinner || game.teamBReportedWinner) && (
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '8px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      {game.teamAReportedWinner && (
                        <span>{teamA.teamName} reported: <strong style={{ color: '#94a3b8' }}>{getTeamName(game.teamAReportedWinner)}</strong></span>
                      )}
                      {game.teamBReportedWinner && (
                        <span>{teamB?.teamName || 'Team B'} reported: <strong style={{ color: '#94a3b8' }}>{getTeamName(game.teamBReportedWinner)}</strong></span>
                      )}
                    </div>
                  )}

                  {/* Winner selection buttons */}
                  {gameRelevant ? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        style={{
                          flex: 1, padding: '8px 12px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                          border: selectedWinner === teamA.teamId ? '2px solid #38bdf8' : '1px solid rgba(148, 163, 184, 0.2)',
                          backgroundColor: selectedWinner === teamA.teamId ? 'rgba(56, 189, 248, 0.12)' : 'rgba(148, 163, 184, 0.06)',
                          color: selectedWinner === teamA.teamId ? '#38bdf8' : '#94a3b8',
                          transition: 'all 0.15s ease',
                        }}
                        onClick={() => handleGameSelect(game.gameNumber, teamA.teamId)}
                        disabled={isSubmitting}
                      >
                        {teamA.teamTag || teamA.teamName}
                      </button>
                      {teamB && (
                        <button
                          type="button"
                          style={{
                            flex: 1, padding: '8px 12px', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
                            border: selectedWinner === teamB.teamId ? '2px solid #38bdf8' : '1px solid rgba(148, 163, 184, 0.2)',
                            backgroundColor: selectedWinner === teamB.teamId ? 'rgba(56, 189, 248, 0.12)' : 'rgba(148, 163, 184, 0.06)',
                            color: selectedWinner === teamB.teamId ? '#38bdf8' : '#94a3b8',
                            transition: 'all 0.15s ease',
                          }}
                          onClick={() => handleGameSelect(game.gameNumber, teamB.teamId)}
                          disabled={isSubmitting}
                        >
                          {teamB.teamTag || teamB.teamName}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontSize: '0.75rem', color: '#475569', fontStyle: 'italic' }}>
                      Series already decided
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Series score summary */}
          {Object.keys(gameSelections).length > 0 && (
            <div style={{
              padding: '12px 16px', borderRadius: '8px', margin: '16px 0', textAlign: 'center',
              backgroundColor: seriesDecided ? 'rgba(74, 222, 128, 0.08)' : 'rgba(148, 163, 184, 0.08)',
              border: `1px solid ${seriesDecided ? 'rgba(74, 222, 128, 0.25)' : 'rgba(148, 163, 184, 0.15)'}`,
            }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: seriesDecided ? '#4ade80' : '#94a3b8' }}>
                {teamA.teamName} {teamAWins} — {teamBWins} {teamB?.teamName || 'Team B'}
              </div>
              {seriesDecided && seriesWinnerName && (
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }}>
                  {seriesWinnerName} wins the series
                </div>
              )}
              {!seriesDecided && (
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '4px' }}>
                  Select winners for remaining games to decide the series
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginBottom: '16px' }}>
          Dismissing this dispute will leave game results unchanged. Provide a reason below.
        </p>
      )}

      {/* Resolution notes */}
      <div className="sl-dispute-resolution-notes-group">
        <label className="sl-dispute-filter-label" htmlFor="resolution-notes">
          Resolution Notes <span className="sl-required">*</span>
        </label>
        <textarea
          id="resolution-notes"
          className="sl-dispute-resolution-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Explain the reasoning for this resolution..."
          rows={4}
        />
      </div>

      {/* Actions */}
      <div className="sl-dispute-resolution-actions" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {!isDismissing ? (
          <>
            <Button
              type="submit"
              variant="primary"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Resolving...' : 'Confirm Resolution'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDismissing(true)}
              disabled={isSubmitting}
            >
              Dismiss Dispute Instead
            </Button>
          </>
        ) : (
          <>
            <Button
              type="submit"
              variant="primary"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Dismissing...' : 'Confirm Dismissal'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDismissing(false)}
              disabled={isSubmitting}
            >
              Back to Game Results
            </Button>
          </>
        )}
      </div>
    </form>
  );
}